import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/query-client";
import { Bell, Building2, ChevronRight, Menu, X, FileText, User, LogOut, Settings, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";

import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { roleDisplayName } from "@shared/roleLabels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Breadcrumb {
  label: string;
  href?: string;
  current?: boolean;
}

interface HeaderProps {
  breadcrumbs?: Breadcrumb[];
  onMenuClick?: () => void;
}

export default function Header({ breadcrumbs = [], onMenuClick }: HeaderProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [recentClaimsOpen, setRecentClaimsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<Set<string>>(new Set());
  const [profileImageError, setProfileImageError] = useState(false);

  const { toast} = useToast();

  // Reset profile image error when URL changes (e.g., after upload)
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.profileImageUrl]);

  // Fetch new notifications API instead of legacy recentActivity
  const { data: notifications = [] } = useQuery<Array<any>>({
    queryKey: ["/api/notifications"],
  });

  const { data: unreadCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/notifications/unread-count"],
  });
  
  const unreadCount = unreadCountData?.count || 0;

  const { data: recentClaims = [], isLoading: isLoadingRecentClaims } = useQuery<Array<{
    id: string;
    claimNumber: string;
    policyholderFirstName: string | null;
    policyholderLastName: string | null;
    policyholderCompany: string | null;
    status: string;
    updatedAt: string;
  }>>({
    queryKey: ["/api/claims/recent"],
    staleTime: 60_000,
  });

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    if (!firstName && !lastName) return "U";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const handleNotificationClick = async (notification: any) => {
    setNotificationsOpen(false);
    
    if (!notification.readAt) {
      apiRequest('PATCH', `/api/notifications/${notification.id}/read`, {})
        .catch(console.error)
        .finally(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
          queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
        });
    }

    if (notification.deepLink) {
      window.open(notification.deepLink, '_blank');
    }
  };

  const handleClearNotifications = async () => {
    if (notifications && notifications.length > 0) {
      try {
        const response = await apiRequest('POST', '/api/notifications/mark-all-read', {});
        
        if (response.ok) {
          await queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
          await queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
          setNotificationsOpen(false);
          toast({
            title: "Notifications cleared",
            description: "All notifications marked as read.",
            duration: 2000
          });
        } else {
          throw new Error('Failed to mark all as read');
        }
      } catch (error) {
        console.error('Error clearing notifications:', error);
        toast({
          title: "Error",
          description: "Failed to clear notifications. Please try again.",
          variant: "destructive",
          duration: 3000
        });
      }
    } else {
      setNotificationsOpen(false);
    }
  };

  const activeNotifications = notifications || [];

  return (
    <header className="bg-card border-b border-border">
      <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 lg:px-6 gap-2">
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden flex-shrink-0 h-9 w-9 p-0"
            onClick={onMenuClick}
            data-testid="button-mobile-menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="hidden lg:block flex-shrink-0">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                {breadcrumbs.map((breadcrumb, index) => (
                  <li key={index} className="flex items-center">
                    {index > 0 && (
                      <ChevronRight className="text-muted-foreground h-4 w-4 mx-2" />
                    )}
                    {breadcrumb.current ? (
                      <span className="text-foreground text-sm font-medium">
                        {breadcrumb.label}
                      </span>
                    ) : (
                      <Link
                        href={breadcrumb.href || "/"}
                        className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                      >
                        {breadcrumb.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          {/* Recent Claims Popover */}
          <div className="flex-1 max-w-lg hidden sm:block">
            <Popover open={recentClaimsOpen} onOpenChange={setRecentClaimsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-muted-foreground h-9 font-normal"
                  data-testid="button-recent-claims"
                >
                  <Clock className="mr-2 h-4 w-4 flex-shrink-0" />
                  Recent Claims
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className="p-3 border-b">
                  <h3 className="text-sm font-semibold text-foreground">Recent Claims</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Recently updated</p>
                </div>
                <div>
                  {isLoadingRecentClaims ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      Loading...
                    </div>
                  ) : recentClaims.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No claims found
                    </div>
                  ) : (
                    recentClaims.map((claim, index) => {
                      const name = [claim.policyholderFirstName, claim.policyholderLastName]
                        .filter(Boolean).join(" ") || claim.policyholderCompany || "Unknown";
                      return (
                        <Link
                          key={claim.id}
                          href={`/claims/${claim.id}`}
                          className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent/50 transition-colors border-b last:border-b-0 no-underline"
                          onClick={() => setRecentClaimsOpen(false)}
                          data-testid={`link-recent-claim-${index}`}
                        >
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {claim.claimNumber}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{name}</p>
                          </div>
                          <Badge variant="secondary" className="text-xs capitalize flex-shrink-0">
                            {claim.status}
                          </Badge>
                        </Link>
                      );
                    })
                  )}
                </div>
                <div className="p-2 border-t">
                  <Link
                    href="/claims"
                    className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1 no-underline"
                    onClick={() => setRecentClaimsOpen(false)}
                    data-testid="link-all-claims"
                  >
                    View all claims
                  </Link>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="relative text-muted-foreground hover:text-foreground h-9 w-9 p-0"
                data-testid="button-notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 sm:w-96 p-0" align="end">
              <div className="p-3 sm:p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Notifications</h3>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearNotifications}
                    className="h-8 text-xs"
                    data-testid="button-clear-notifications"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Mark All Read
                  </Button>
                )}
              </div>
              <ScrollArea className="h-80 sm:h-96">
                {!activeNotifications || activeNotifications.length === 0 ? (
                  <div className="p-6 sm:p-8 text-center text-muted-foreground">
                    <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {activeNotifications.map((activity: any) => (
                      <div 
                        key={activity.id} 
                        className={`p-3 sm:p-4 hover:bg-accent/50 transition-colors cursor-pointer ${!activity.readAt ? 'bg-primary/5' : ''}`}
                        onClick={() => handleNotificationClick(activity)}
                        data-testid={`notification-${activity.id}`}
                      >
                        <p className={`text-sm text-foreground mb-1 ${!activity.readAt ? 'font-semibold' : 'font-medium'}`}>
                          {activity.title}
                        </p>
                        <p className="text-sm text-foreground/80 mb-2">
                          {activity.message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-medium text-foreground" data-testid="text-user-name">
                      {user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user?.email || "User"
                      }
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="text-user-role">
                      {user?.role ? roleDisplayName(user.roleName ?? user.role) : "Adjuster"}
                    </p>
                  </div>
                  
                  {user?.profileImageUrl && !profileImageError ? (
                    <img
                      src={user.profileImageUrl}
                      alt="Profile"
                      className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                      data-testid="img-user-avatar"
                      onError={() => setProfileImageError(true)}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0" data-testid="div-user-initials">
                      <span className="text-primary-foreground text-sm font-medium">
                        {getInitials(user?.firstName, user?.lastName)}
                      </span>
                    </div>
                  )}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Array.isArray(user?.orgs) && user.orgs.length > 1 && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link href="/select-org" className="flex items-center cursor-pointer">
                        <Building2 className="mr-2 h-4 w-4" />
                        <span>Switch Organizations</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem asChild>
                  <Link href="/profile" className="flex items-center cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/api/logout" className="flex items-center cursor-pointer text-red-600 focus:text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign Out</span>
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
    </header>
  );
}